/* SUMMARY
  * Imports
  * lazyWithReload: React.lazy that recovers from a stale page
*/

/* Imports */
import { ComponentType, lazy, LazyExoticComponent } from "react";
/***/

// Une seule tentative de rechargement par onglet : si le module échoue encore après, le problème
// n'est pas la fraîcheur de la page et il faut laisser l'erreur remonter plutôt que boucler.
const RELOAD_FLAG = "lazyChunkReloaded";

/**
* lazyWithReload - React.lazy qui recharge la page une fois si l'import dynamique échoue
*
* Un `import()` échoue avec "Failed to fetch dynamically imported module" quand le bundle demandé
* n'est plus celui que la page a chargé :
*   - en dev, Vite ré-optimise ses dépendances (ajout d'un paquet, cache .vite vidé, changement de
*     branche) et répond 504 "Outdated Optimize Dep" sur les URL portant l'ancien hash `?v=` ;
*   - en prod, un déploiement remplace les chunks hashés et l'ancien nom renvoie 404.
* Dans les deux cas le code est bon, c'est la page ouverte qui est périmée : un rechargement suffit.
*
* @param factory - Import dynamique du module
*/
export default function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err) {
      if (sessionStorage.getItem(RELOAD_FLAG)) throw err; // Déjà rechargé : échec réel

      console.error("Stale chunk, reloading once", err);
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();

      // Suspend le rendu le temps que le rechargement prenne la main : résoudre ou rejeter ici
      // ferait clignoter un écran d'erreur sur une page qui est déjà en train de partir.
      return new Promise<never>(() => {});
    }
  });
}
/***/
